import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { employeeADLogin, verifyEmployeeOtp } from "../Services/authService";
import NormalAlert from "../../Alerts/NormalAlert";
import LoginBg from "../../../Assets/Loginbg.svg";
import { Lock, Eye, EyeOff, User, RefreshCw } from "lucide-react";

const EmployeeLogin = () => {
	const navigate = useNavigate();

	const [employeeCode, setEmployeeCode] = useState("");
	const [password, setPassword] = useState("");
	const [otp, setOtp] = useState("");
	const [authUID, setAuthUID] = useState(null);
	const [isOtpStep, setIsOtpStep] = useState(false);

	const [errors, setErrors] = useState({});
	const [alert, setAlert] = useState(null);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	// Captcha
	const [captchaText, setCaptchaText] = useState("");
	const [captchaInput, setCaptchaInput] = useState("");

	const generateCaptcha = () => {
		const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		const captcha = Array.from({ length: 6 }, () =>
			chars.charAt(Math.floor(Math.random() * chars.length)),
		).join("");

		setCaptchaText(captcha);
		setCaptchaInput("");
	};

	useEffect(() => {
		const canvas = document.getElementById("captchaCanvas");
		if (!canvas) return;
		const ctx = canvas.getContext("2d");

		ctx.clearRect(0, 0, canvas.width, canvas.height);

		ctx.fillStyle = "#EAF4FF";
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		if (!captchaText) return;

		for (let i = 0; i < 5; i++) {
			ctx.strokeStyle = `rgba(0, 70, 135, ${Math.random() * 0.5 + 0.3})`;
			ctx.beginPath();
			ctx.moveTo(Math.random() * 150, Math.random() * 45);
			ctx.lineTo(Math.random() * 150, Math.random() * 45);
			ctx.stroke();
		}

		ctx.font = "bold 24px monospace";
		ctx.textBaseline = "middle";

		for (let i = 0; i < captchaText.length; i++) {
			const angle = (Math.random() - 0.5) * 0.4;
			ctx.save();
			ctx.translate(15 + i * 20, 22);
			ctx.rotate(angle);
			ctx.fillStyle = `rgb(${50 + Math.random() * 100}, ${
				60 + Math.random() * 100
			}, ${120 + Math.random() * 100})`;
			ctx.fillText(captchaText[i], 0, 0);
			ctx.restore();
		}

		for (let i = 0; i < 30; i++) {
			ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.3})`;
			ctx.beginPath();
			ctx.arc(Math.random() * 150, Math.random() * 45, 1, 0, 2 * Math.PI);
			ctx.fill();
		}
	}, [captchaText]);

	useEffect(() => {
		generateCaptcha();
	}, []);

	// STEP 1 LOGIN
	const handleLogin = async (e) => {
		e.preventDefault();
		setErrors({});
		setAlert(null);

		const validationErrors = {};

		if (!employeeCode.trim())
			validationErrors.employeeCode = "Employee Code is required.";

		if (!password.trim()) validationErrors.password = "Password is required.";

		if (!captchaInput.trim()) {
			validationErrors.captcha = "Captcha is required.";
		} else if (captchaInput.trim().toUpperCase() !== captchaText) {
			validationErrors.captcha = "Captcha does not match.";
		}

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			generateCaptcha();
			return;
		}

		try {
			setLoading(true);

			const response = await employeeADLogin(employeeCode, password);
			const data = response.data;

			if (data.status === "Success" && data.userDetails?.token) {
				Cookies.set("employeeToken", data.userDetails.token, {
					secure: true,
					sameSite: "Strict",
				});
				// setAlert({ type: "success", message: "OTP verified successfully!" });
				setAlert({
					type: "fail",
					message: data.message || "Verification successfully.",
				});
				setTimeout(() => navigate("/Dashboard"), 1500);
			} else {
				setAlert({
					type: "fail",
					message: data.message || "OTP verification failed.",
				});
			}
		} catch (error) {
			setAlert({
				type: "fail",
				message: error?.response?.data?.message || "Something went wrong",
			});
			generateCaptcha();
		} finally {
			setLoading(false);
		}
	};

	// STEP 2 OTP VERIFY
	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		setErrors({});
		setAlert(null);

		if (!otp.trim()) {
			setErrors({ otp: "OTP is required" });
			return;
		}

		try {
			setLoading(true);

			const response = await verifyEmployeeOtp(otp, authUID);
			const data = response.data;

			if (data.status === "Success" && data.userDetails?.token) {
				Cookies.set("employeeToken", data.userDetails.token, {
					secure: true,
					sameSite: "Strict",
					expires: 1,
				});

				setAlert({ type: "success", message: "Login successful!" });

				setTimeout(() => navigate("/Dashboard"), 1000);
			} else {
				setAlert({
					type: "fail",
					message: data.message || "Invalid OTP",
				});
			}
		} catch (error) {
			setAlert({
				type: "fail",
				message: error?.response?.data?.message || "OTP verification failed",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<div className="min-h-screen flex flex-col md:flex-row animate-fadeIn">
				{/* RIGHT */}
				<div className="hidden md:block md:w-1/2">
					<img
						src={LoginBg}
						alt="HR Setu login background showing modern workspace"
						className="w-full h-screen object-cover"
					/>
				</div>
				{/* LEFT */}
				<div className="relative w-full md:w-1/2 flex flex-col justify-start pt-[24vh] px-8 md:px-20 pb-10 bg-white">
					<div className="absolute top-0 left-0 w-full h-[22vh] bg-gradient-to-br from-[#e3f2fd] via-[#bbdefb] to-[#90caf9] flex items-center justify-center">
						<Link to="/">
							<img
								src={process.env.PUBLIC_URL + "/HRSetu-final-logo.svg"}
								alt="HR Setu recruitment platform logo"
								className="w-28"
							/>
						</Link>
					</div>

					<div className="relative z-10">
						<div className="text-center mb-6">
							<h4 className="text-xl font-semibold text-gray-800 mt-10">
								{isOtpStep ? "Enter OTP" : "Login to your account"}
							</h4>
							<p className="text-gray-500">
								{isOtpStep
									? "Enter the OTP sent to your email"
									: "Enter your System Credentials to login"}
							</p>
						</div>

						<form
							onSubmit={isOtpStep ? handleVerifyOtp : handleLogin}
							className="space-y-4 px-4 md:px-10"
						>
							{/* LOGIN STEP */}
							{!isOtpStep && (
								<>
									{/* Employee Code */}
									<div>
										<label className="text-sm">Employee Code</label>
										<div className="relative">
											<User
												className="absolute left-3 top-3 text-gray-400"
												size={18}
											/>
											<input
												value={employeeCode}
												onChange={(e) => {
													let value = e.target.value;

													// Convert to uppercase
													value = value.toUpperCase();

													// Allow only A-Z and 0-9
													value = value.replace(/[^A-Z0-9]/g, "");

													setEmployeeCode(value);
												}}
												autoComplete="off"
												placeholder="Employee Code"
												className="w-full pl-10 py-3 border rounded-lg bg-blue-50"
											/>
										</div>
										{errors.employeeCode && (
											<p className="text-red-500 text-sm">
												{errors.employeeCode}
											</p>
										)}
									</div>

									{/* Password */}
									<div>
										<label className="text-sm">Password</label>
										<div className="relative">
											<Lock
												className="absolute left-3 top-3 text-gray-400"
												size={18}
											/>
											<input
												type={isPasswordVisible ? "text" : "password"}
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												placeholder="Password"
												className="w-full pl-10 pr-10 py-3 border rounded-lg bg-blue-50"
											/>
											<button
												type="button"
												className="absolute right-3 top-3"
												onClick={() => setIsPasswordVisible(!isPasswordVisible)}
											>
												{isPasswordVisible ? (
													<EyeOff size={18} />
												) : (
													<Eye size={18} />
												)}
											</button>
										</div>
										{errors.password && (
											<p className="text-red-500 text-sm">{errors.password}</p>
										)}
									</div>

									{/* CAPTCHA */}
									<div>
										<label className="text-sm">Verification Code</label>
										<div className="flex items-center gap-3">
											<canvas
												id="captchaCanvas"
												width="150"
												height="45"
												className="border rounded"
											/>
											<button type="button" onClick={generateCaptcha}>
												<RefreshCw size={20} />
											</button>
											<input
												value={captchaInput}
												onChange={(e) => setCaptchaInput(e.target.value)}
												placeholder="Enter Captcha"
												className="flex-1 px-3 py-2 border rounded"
											/>
										</div>
										{errors.captcha && (
											<p className="text-red-500 text-sm">{errors.captcha}</p>
										)}
									</div>
								</>
							)}

							{/* OTP STEP */}
							{isOtpStep && (
								<div>
									<label className="text-sm">Enter OTP</label>
									<input
										value={otp}
										onChange={(e) => setOtp(e.target.value)}
										className="w-full px-3 py-3 border rounded-lg bg-blue-50"
									/>
									{errors.otp && (
										<p className="text-red-500 text-sm">{errors.otp}</p>
									)}
								</div>
							)}

							{/* BUTTON */}
							<div className="flex justify-center pt-4">
								<button
									disabled={loading}
									className={`w-[190px] h-[48px] text-white rounded-md font-semibold flex justify-center items-center shadow-md transition-all duration-300 ${
										loading
											? "bg-gray-400 cursor-not-allowed"
											: "bg-sky-500 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
									}`}
								>
									{loading
										? isOtpStep
											? "Verifying..."
											: "Loading..."
										: isOtpStep
											? "Verify OTP"
											: "Login"}
								</button>
							</div>
							<div className="text-center mt-3">
								<p className="text-sm text-gray-500">
									Do not have System Login?{" "}
									<span
										className="text-blue-600 font-medium cursor-pointer hover:underline"
										onClick={() => navigate("/Employee-Login")}
									>
										Go to Email Login
									</span>
								</p>
							</div>
						</form>
					</div>

					<div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[85%] text-center text-xs text-gray-400 border-t border-gray-200 py-2">
						Designed & Developed by
						<span className="text-blue-500"> VRSamadhan Tech Solution</span>
					</div>
				</div>
			</div>

			{alert && (
				<NormalAlert
					type={alert.type}
					message={alert.message}
					onClose={() => setAlert(null)}
				/>
			)}
		</>
	);
};

export default EmployeeLogin;
